import BarFill from '@/components/charts/BarFill'
import Tooltip from '@/components/ui/Tooltip'
import { cn } from '@/lib/utils'

const TONES = {
  up: 'bg-market-up-fill',
  down: 'bg-market-down-fill',
  flat: 'bg-market-flat',
  accent: 'bg-tone-green',
  saffron: 'bg-tone-saffron',
  // The same gold at a third: the second step of a two-step scale (Large trades' size split).
  saffronSoft: 'bg-tone-saffron/35',
  violet: 'bg-tone-violet',
  teal: 'bg-tone-teal',
  muted: 'bg-muted',
  // A segment that is "no category", drawn and labelled: the subtle grey, which
  // clears 5:1 on the card in both themes. `muted` is the empty track's own
  // fill (1.1:1 on a light card), so a segment in it would read as a gap.
  neutral: 'bg-muted-foreground-subtle',
} as const

/**
 * Bar thickness on the 4-grid. 4 is the bar in a table cell (the Sectors
 * breadth column, `4236:108727`); 8 is a card's. 6 is the thickness every
 * caller measured before the 2026-09-28 design refresh, snapped to 8 (a tie
 * goes to the multiple of 8) and kept as a key so those callers still compile.
 */
const HEIGHTS = { 4: 'h-1', 6: 'h-2', 8: 'h-2' } as const

export interface BarSegment {
  value: number
  tone: keyof typeof TONES
  label: string
  /**
   * The chip over this segment on hover or focus — "Bought 93.5 Cr" (the mock's `mm-seg`
   * `data-tip`). Give every segment one to make the bar answer the pointer: the others dim to .35
   * and the hovered one brightens (`seg-hover`), and each segment becomes a keyboard stop.
   */
  tip?: string
}

interface SegmentedBarProps {
  segments: readonly BarSegment[]
  height?: keyof typeof HEIGHTS
  /** Names the whole bar; each segment's share is appended for the label. */
  ariaLabel: string
  /**
   * The segment (by `label`) a figure outside the bar points at while that figure is hovered or
   * focused (sz:sa, the Signal dashboard's counts, mock `szd-sum__bar`): it grows to fill the
   * bar's box and the others fall to .3. Passing it at all, `null` included, sets the bar in a
   * 12px box with 8px runs so the named one has room to grow; `null` names none. Tipped bars only.
   */
  emphasis?: string | null | undefined
  className?: string
}

/**
 * The allocation and breadth bars: a few coloured runs, no axis, no numbers.
 * Square-ended, like everything else in V2 (`6144:4`, radius 0), with the runs
 * butted together: each is its own colour, and the old 2px gap was off the
 * 4-grid.
 *
 * The runs grow in from the left once on mount, 40ms apart, each from its own
 * edge, so the bar fills like a tide rather than all at once.
 *
 * Widths come from flex-grow rather than percentage widths, so a segment never
 * rounds past the track's end.
 *
 * Sub-100 inputs (an allocation of 41/26/14) keep their real proportions and
 * leave the rest of the track empty; inputs that are already a whole (78 rose,
 * 142 fell, 21 flat) fill it. Hence the denominator, not a normalise step.
 *
 * In a table cell, give it a width (`className="w-20"`): a bar has no content
 * of its own, so on the table's auto layout its column would otherwise shrink
 * to the header's width.
 */
export default function SegmentedBar({
  segments,
  height = 8,
  ariaLabel,
  emphasis,
  className,
}: SegmentedBarProps) {
  const total = segments.reduce((sum, s) => sum + Math.max(s.value, 0), 0)
  const denominator = Math.max(total, 100)
  const remainder = denominator - total

  const share = (value: number) => Math.round((Math.max(value, 0) / denominator) * 100)
  const description = segments.map((s) => `${s.label} ${String(share(s.value))}%`).join(', ')

  if (segments.length > 0 && segments.every((segment) => segment.tip !== undefined)) {
    // A group of named segments rather than one picture: each is focusable for its chip, so it
    // has to be in the accessibility tree, where a `role="img"` would hide it.
    return (
      <div
        role="group"
        aria-label={ariaLabel}
        className={cn(
          'seg-hover flex w-full',
          emphasis === undefined ? HEIGHTS[height] : 'h-3 items-center',
          className,
        )}
      >
        {segments.map((segment, index) => (
          <Tooltip
            key={segment.label}
            title={segment.tip}
            variant="chip"
            side="above"
            align="center"
          >
            <BarFill
              as="span"
              index={index}
              data-seg=""
              role="img"
              aria-hidden={false}
              aria-label={`${segment.label} ${String(share(segment.value))}%`}
              tabIndex={0}
              className={cn(
                'block cursor-default focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring',
                emphasis === undefined
                  ? 'h-full'
                  : emphasis === segment.label
                    ? 'h-3'
                    : ['h-2', emphasis !== null && 'opacity-30'],
                TONES[segment.tone],
              )}
              style={{ flexGrow: Math.max(segment.value, 0), flexBasis: 0 }}
            />
          </Tooltip>
        ))}
        {remainder > 0 ? (
          <span className="bg-muted" style={{ flexGrow: remainder, flexBasis: 0 }} />
        ) : null}
      </div>
    )
  }

  return (
    <div
      role="img"
      aria-label={`${ariaLabel}: ${description}`}
      className={cn('flex w-full overflow-hidden', HEIGHTS[height], className)}
    >
      {segments.map((segment, index) => (
        <BarFill
          key={segment.label}
          index={index}
          className={TONES[segment.tone]}
          style={{ flexGrow: Math.max(segment.value, 0), flexBasis: 0 }}
        />
      ))}
      {remainder > 0 ? (
        <div className="bg-muted" style={{ flexGrow: remainder, flexBasis: 0 }} />
      ) : null}
    </div>
  )
}
